/** GTM mission pack — durable content + helpers. */

import missionsDoc from "../data/gtm-missions-v1.json";
import questsDoc from "../data/gtm-mission-quests-v1.json";

export type AutomationLevel = "A0" | "A1" | "A2" | "A3" | "A4" | "A5";

export type MissionProgressStatus =
  | "locked"
  | "available"
  | "active"
  | "completed";

export type MissionChannel =
  | "SETUP"
  | "SEO"
  | "SOME"
  | "VIDEO"
  | "ARTICLE"
  | "LANDING"
  | "COLD"
  | "LEARN"
  | "OUTCOME";

export interface AutomationLevelDef {
  level: AutomationLevel;
  name: string;
  label: string;
  rule: string;
}

export interface MissionDefinition {
  missionId: string;
  code: string;
  slug: string;
  patternId: string;
  name: string;
  shortName: string;
  objective: string;
  channel: MissionChannel;
  funnelStage: string;
  timebox: string;
  targetAutomationLevel: AutomationLevel;
  proofContract: string;
  successSignals: string[];
  failureSignals: string[];
  humanOnlySteps: string[];
  nextMissionId: string | null;
  prerequisiteMissionId: string | null;
  firstQuestId: string;
}

export interface MissionQuestDefinition {
  id: string;
  action: string;
  missionId: string;
  missionCode: string;
  missionName: string;
  missionShortName: string;
  automationLevel: AutomationLevel;
  previousLevel: number | null;
  recommendedLevel: number;
  minLevel: number;
  funnelStage: string;
  complexity: 1 | 2 | 3 | 4 | 5;
  executionXp: number;
  evidenceBonusXp: number;
  totalXp: number;
  executableBy: "human" | "agent" | "either";
  title: string;
  brief: string;
  steps: string[];
  timebox: string;
  mergeInto: string | null;
  prerequisites: string[];
  requires: string | null;
  nextQuestId: string | null;
  proof: string;
  successSignals: string[];
  failureSignals: string[];
  humanOnlyJudgment: string[];
  channel: MissionChannel;
  pack: string;
}

export interface ProductMissionProgress {
  id: string;
  organizationId: string;
  productId: string;
  missionId: string;
  currentAutomationLevel: AutomationLevel;
  status: MissionProgressStatus;
  goalId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  lastAdvancedAt: string | null;
  proofSummary: string | null;
  createdAt: string;
}

export const GTM_MISSION_PACK_VERSION = (missionsDoc as { catalogVersion: string })
  .catalogVersion;

export const GTM_MISSION_PACK_ID = (missionsDoc as { packId: string }).packId;

export const AUTOMATION_LEVEL_ORDER: AutomationLevel[] = [
  "A0",
  "A1",
  "A2",
  "A3",
  "A4",
  "A5",
];

export function listAutomationLevels(): AutomationLevelDef[] {
  return (missionsDoc as { automationLevels: AutomationLevelDef[] })
    .automationLevels;
}

export function listMissions(): MissionDefinition[] {
  return (missionsDoc as { missions: MissionDefinition[] }).missions;
}

export function getMission(missionId: string): MissionDefinition | null {
  return listMissions().find((m) => m.missionId === missionId) ?? null;
}

export function listMissionQuests(): MissionQuestDefinition[] {
  return (questsDoc as { quests: MissionQuestDefinition[] }).quests;
}

export function getMissionQuest(id: string): MissionQuestDefinition | null {
  return listMissionQuests().find((q) => q.id === id) ?? null;
}

export function listQuestsForMission(
  missionId: string,
): MissionQuestDefinition[] {
  return listMissionQuests().filter((q) => q.missionId === missionId);
}

export function automationRank(level: AutomationLevel): number {
  return AUTOMATION_LEVEL_ORDER.indexOf(level);
}

export function automationAtLeast(
  current: AutomationLevel,
  required: AutomationLevel,
): boolean {
  return automationRank(current) >= automationRank(required);
}

export function nextAutomationLevel(
  current: AutomationLevel,
): AutomationLevel | null {
  const i = automationRank(current);
  if (i < 0 || i >= AUTOMATION_LEVEL_ORDER.length - 1) return null;
  return AUTOMATION_LEVEL_ORDER[i + 1]!;
}

/** Compare A0 < A1 < … for unlock gating. */
export function isAutomationLocked(
  progressLevel: AutomationLevel,
  questLevel: AutomationLevel,
): boolean {
  return !automationAtLeast(progressLevel, questLevel);
}
